import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { allRecruitJobs } from '../data/recruitData';

const RecruitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const job = allRecruitJobs.find(item => item.id === Number(id));

  if (!job) {
    return (
      <div className="pt-40 pb-20 text-center min-h-screen bg-gray-50">
        <h2 className="text-2xl font-bold text-gray-800">求人情報が見つかりませんでした。</h2>
        <Link to="/recruit" className="text-[#3aabd2] mt-4 inline-block font-semibold hover:underline">
          採用情報一覧に戻る
        </Link>
      </div>
    );
  }

  const mailtoLink = `mailto:info@aglab.co.jp?subject=${encodeURIComponent(job.mailtoSubject)}&body=${encodeURIComponent(job.mailtoBody)}`;

  return (
    <div className="pt-20 bg-white min-h-screen text-slate-800 antialiased selection:bg-slate-900 selection:text-white pb-28 sm:pb-32">
      {/* パンくずリスト */}
      <div className="border-b border-slate-200 bg-slate-50/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/" className="hover:text-slate-900 transition-colors">TOP</Link>
            <svg className="w-3.5 h-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/recruit" className="hover:text-slate-900 transition-colors text-slate-600">採用情報</Link>
            <svg className="w-3.5 h-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-slate-900 font-medium truncate">{job.shortTitle}</span>
          </nav>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pb-20">
        {/* 採用一覧への戻りリンク */}
        <div className="mb-6">
          <Link
            to="/recruit"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>採用情報一覧に戻る</span>
          </Link>
        </div>

        {/* ヒーローセクション */}
        <section className="space-y-6 pb-10 border-b border-slate-200">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-950 tracking-tight leading-snug">
            {job.title}
          </h1>

          <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
            {job.subCopy}
          </p>

          <div className="border-t border-b border-slate-200 py-4 bg-slate-50/40 rounded-lg px-4 sm:px-6">
            <dl className="divide-y divide-slate-200/80 text-xs sm:text-sm">
              <div className="py-2.5 sm:grid sm:grid-cols-4 sm:gap-4 items-center">
                <dt className="font-bold text-slate-900 mb-0.5 sm:mb-0">給与</dt>
                <dd className="sm:col-span-3 text-slate-800">
                  <span className="font-bold text-slate-950 text-sm sm:text-base mr-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {job.salary}
                  </span>
                  <span className="text-xs text-slate-500">
                    {job.salaryNote}
                  </span>
                </dd>
              </div>
              <div className="py-2.5 sm:grid sm:grid-cols-4 sm:gap-4 items-center">
                <dt className="font-bold text-slate-900 mb-0.5 sm:mb-0">勤務形態</dt>
                <dd className="sm:col-span-3 text-slate-800">
                  <span className="font-semibold text-slate-900 mr-2">{job.workStyle}</span>
                  <span className="text-xs text-slate-500">（日本全国どこからでも在宅勤務可能）</span>
                </dd>
              </div>
              <div className="py-2.5 sm:grid sm:grid-cols-4 sm:gap-4 items-center">
                <dt className="font-bold text-slate-900 mb-0.5 sm:mb-0">勤務時間</dt>
                <dd className="sm:col-span-3 text-slate-800">
                  <span className="font-semibold text-slate-900 mr-2">{job.workingHours}</span>
                  <span className="text-xs text-slate-500">{job.workingHoursNote}</span>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* 01. なにをやっているのか（Mission & About） */}
        <section className="py-12 border-b border-slate-200 space-y-6">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              01. MISSION &amp; ABOUT
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              なにをやっているのか
            </h2>
          </div>

          <div className="space-y-4 text-sm sm:text-base text-slate-700 leading-relaxed max-w-3xl">
            <p className="font-bold text-slate-900 text-lg">
              「AIで日本をアップデートする」
            </p>
            <p>
              株式会社AgLabは、企業の生成AI導入やDX推進を総合支援するAIソリューション企業です。生成AI研修や企業専属のAI顧問サービス「AIKATA」、コンサルティング、カスタムAI受託開発など、現場実装に徹底的にこだわった事業を展開しています。
            </p>
            <p>
              AI市場の急速な拡大に伴い、受講企業やパートナー企業からのご依頼が急速に増加しています。そこで、研修やコンサルティング業務が円滑に進行するよう、受講者リストの管理や書類作成、日程・メッセージ対応などのバックオフィスを支えてくださる新しい仲間を募集します。
            </p>
          </div>
        </section>

        {/* 02. AgLabが選ばれる3つの魅力（Why Work Here） */}
        <section className="py-12 border-b border-slate-200 space-y-8">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              02. WHY WORK HERE
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              AgLabが選ばれる3つの理由
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {job.features.map((feat, index) => (
              <div key={index} className="p-6 rounded-xl border border-slate-200 bg-white space-y-3">
                <div className="text-xs font-bold text-blue-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {feat.tag}
                </div>
                <h3
                  className="text-base sm:text-lg font-bold text-slate-950 leading-snug"
                  dangerouslySetInnerHTML={{ __html: feat.title }}
                />
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {feat.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 03. 具体的な仕事内容（Job Description） */}
        <section className="py-12 border-b border-slate-200 space-y-8">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              03. JOB DESCRIPTION
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              仕事内容
            </h2>
            <p className="text-sm text-slate-600">
              AI研修やコンサルティングを受講する企業様のバックオフィス業務全般をご担当いただきます。
            </p>
          </div>

          <div className="space-y-4">
            {job.jobDescriptions.map((item, index) => (
              <div key={index} className="p-4 sm:p-5 rounded-xl border border-slate-200 bg-white">
                <div className="font-bold text-slate-900 text-sm sm:text-base mb-1">
                  {item.title}
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* 職場環境・スタイル */}
          <div className="p-5 sm:p-6 rounded-xl border border-slate-200 bg-slate-50/70 space-y-3">
            <div className="font-bold text-slate-900 text-sm">職場環境・スタイル</div>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-600">
              {job.workStyles.map((style, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
                  <span>{style}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 04. 募集要項・待遇条件（Job Requirements） */}
        <section id="spec" className="py-12 border-b border-slate-200 space-y-8">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              04. JOB REQUIREMENTS
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              募集要項・待遇条件
            </h2>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white text-xs sm:text-sm">
            <div className="divide-y divide-slate-200">
              {job.requirements.map((req, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 md:grid-cols-4 p-4 sm:p-5 ${req.isHighlighted ? 'bg-slate-50/60' : ''}`}
                >
                  <div className="font-bold text-slate-900 mb-1 md:mb-0">{req.label}</div>
                  <div className="md:col-span-3 text-slate-800">
                    {req.type === 'text' && (
                      req.isHighlighted ? (
                        <>
                          <div className="text-base sm:text-lg font-bold text-slate-950" style={{ fontFamily: "'Inter', sans-serif" }}>
                            {req.value as string}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {job.salaryNote}
                          </div>
                        </>
                      ) : (
                        <span>{req.value as string}</span>
                      )
                    )}

                    {req.type === 'custom' && Array.isArray(req.value) && (
                      <div className="space-y-1">
                        <div className="font-semibold text-slate-950">{req.value[0]}</div>
                        <div className="text-xs text-slate-500">{req.value[1]}</div>
                      </div>
                    )}

                    {req.type === 'list' && Array.isArray(req.value) && (
                      <div className="space-y-2">
                        <div className="font-semibold text-slate-950">未経験者大歓迎</div>
                        <ul className="list-disc pl-5 space-y-1 text-xs text-slate-600">
                          {req.value.map((v, i) => (
                            <li key={i} className={i === req.value.length - 1 ? 'font-medium text-slate-800' : ''}>
                              {v}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {req.type === 'checklist' && Array.isArray(req.value) && (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                        {req.value.map((v, i) => (
                          <li key={i} className={`flex items-center gap-1.5 ${i === req.value.length - 1 ? 'sm:col-span-2' : ''}`}>
                            <svg className="w-3.5 h-3.5 text-slate-700 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{v}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 05. 選考プロセス（Selection Process） */}
        <section className="py-12 border-b border-slate-200 space-y-8">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              05. SELECTION PROCESS
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              選考プロセス
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {job.selectionSteps.map((step, index) => (
              <div
                key={index}
                className={`p-5 rounded-xl border border-slate-200 space-y-2 ${
                  step.isLast ? 'bg-slate-50' : 'bg-white'
                }`}
              >
                <div
                  className={`text-xs font-bold ${step.isLast ? 'text-blue-700' : 'text-slate-400'}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {step.step}
                </div>
                <div className={`font-bold text-sm ${step.isLast ? 'text-slate-950' : 'text-slate-900'}`}>
                  {step.title}
                </div>
                <p className={`text-xs leading-relaxed ${step.isLast ? 'text-slate-600' : 'text-slate-500'}`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 06. 会社概要（Company Profile） */}
        <section className="py-12 border-b border-slate-200 space-y-8">
          <div className="space-y-1">
            <div className="text-xs font-bold text-slate-400 tracking-wider uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              06. COMPANY PROFILE
            </div>
            <h2 className="text-2xl font-bold text-slate-950 tracking-tight">
              会社概要
            </h2>
          </div>

          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white text-xs sm:text-sm">
            <div className="divide-y divide-slate-200">
              {job.companyProfile.map((item, index) => (
                <div key={index} className="grid grid-cols-1 sm:grid-cols-4 p-4">
                  <div className="font-bold text-slate-900">{item.label}</div>
                  <div className="sm:col-span-3 text-slate-800">
                    {item.isLink ? (
                      <a
                        href={item.value}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
                      >
                        {item.value}
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      item.value
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 採用一覧へ戻るリンク */}
        <div className="py-12 text-center">
          <Link
            to="/recruit"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>採用情報一覧へ戻る</span>
          </Link>
        </div>
      </main>

      {/* 画面最下部固定応募CTAバー */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-slate-200 py-3 sm:py-3.5 px-4 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="max-w-5xl mx-auto flex items-center justify-center">
          <a
            href={mailtoLink}
            className="w-full sm:w-auto min-w-[280px] sm:min-w-[360px] inline-flex items-center justify-center gap-2 px-8 py-3 sm:py-3.5 text-sm sm:text-base font-bold text-white bg-slate-900 hover:bg-slate-800 active:scale-[0.99] rounded-lg transition-all shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>応募する</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default RecruitDetailPage;